/**
 * AddInternalMemberDialog — añade un miembro interno al proyecto.
 *
 * Pickea un user del tenant (vía `GET /tenants/{id}/users?user_status=active`)
 * y lo asigna con uno o más roles + RACI opcional. El backend rechaza users que no
 * sean del tenant activo (defensa además de RLS).
 *
 * Excluye automáticamente users que ya son miembros del proyecto.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  assignOwnerByRole,
  listUnownedByRole,
  type UnownedForRole,
} from '../../../api/artifacts'
import { ApiError } from '../../../api/client'
import {
  addProjectMember,
  type ProjectMember,
  type RaciRole,
} from '../../../api/project_members'
import { listTenantUsers, type TenantUserSummary } from '../../../api/tenants'
import { useAccessToken } from '../../../auth/useAccessToken'
import { Dialog } from '../../Dialog'
import { canJoinSection, canOwnArtifact } from '../../../lib/tiers'
import { pushToast } from '../../../stores/toastStore'
import { RoleChecklist } from './RoleChecklist'
import { defaultInternalRoleKey, type RoleOption } from './roles'

/** Doc 26 §2.2 — modo del diálogo. Define la copy del título/botón +
 * la lógica de default del role:
 *  - `operational`: agrega al equipo operativo (lifecycle). Default
 *    role = primer lead del catálogo (PM). Es el modo legacy.
 *  - `extended`: agrega al equipo extendido (observers). Default role
 *    = primer role del catálogo (no hay "lead" entre observers).
 *
 * El filtrado de `roles` ya viene del padre (`ProjectTeamView`):
 * operational excluye observers + opcionalmente filtra por roles del
 * lifecycle; extended deja solo observers.
 */
export type AddInternalMemberMode = 'operational' | 'extended'

interface AddInternalMemberDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  tenantId: string
  /** Members ya asignados — para excluirlos del picker. */
  existingMembers: ProjectMember[]
  /** Catálogo de role templates ya filtrado por el padre (sección
   * operativa o extendida). El combo de rol los muestra tal cual. */
  roles: RoleOption[]
  /** Doc 26 §2.2 — modo del diálogo. Default `'operational'` para
   * preservar el comportamiento legacy de la llamada existente. */
  mode?: AddInternalMemberMode
}

export function AddInternalMemberDialog({
  open,
  onClose,
  projectId,
  tenantId,
  existingMembers,
  roles,
  mode = 'operational',
}: AddInternalMemberDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  // Default dinámico del role:
  //  · operational → primer lead del catálogo internal (PM, scrum-master,
  //    etc., según `is_project_lead=true`). NUNCA hardcodear `'rol-pm'`.
  //  · extended → primer role del catálogo filtrado (observer). No hay
  //    concepto de "default lead" en el equipo extendido.
  //
  // Si el catálogo no cargó aún, cadena vacía (el render del select
  // muestra el placeholder en su primera <option>).
  const defaultRoles = useMemo(() => {
    const key =
      mode === 'extended'
        ? roles[0]?.template_key
        : defaultInternalRoleKey(roles) ?? roles[0]?.template_key
    return key ? [key] : []
  }, [roles, mode])

  // i18n: el namespace `team.operational.add` / `team.extended.add` ya
  // cubre las copys de cada modo. El diálogo solo elige cuál key usar.
  const titleKey =
    mode === 'extended'
      ? ('team.extended.add' as const)
      : ('team.operational.add' as const)

  const [userId, setUserId] = useState<string>('')
  const [selectedRoles, setSelectedRoles] = useState<string[]>(defaultRoles)
  const [raci, setRaci] = useState<RaciRole | ''>('')
  const [subscribeAll, setSubscribeAll] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // OT-0008 §4.4 — atajo masivo. La resolución automática de dueño corre
  // una sola vez, al materializar el proyecto: quien entra al equipo
  // después no hereda nada. En vez de dejar que el líder asigne documento
  // por documento en el drill-in, tras el alta se ofrece hacerlo de una.
  //
  // `null` = no hay nada que ofrecer (o todavía no se preguntó). Con
  // contenido, el diálogo pasa a su segundo paso.
  const [pendingAssignment, setPendingAssignment] = useState<{
    userId: string
    displayName: string
    roleKeys: string[]
    artifacts: UnownedForRole[]
  } | null>(null)

  const usersQuery = useQuery({
    queryKey: ['tenant', tenantId, 'users', 'active'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listTenantUsers(accessToken, tenantId, { user_status: 'active' })
    },
    enabled: open && !!accessToken && !!tenantId,
    staleTime: 60_000,
  })

  // Usuarios cuya licencia admite esta sección (OT-0058 §4.5: al
  // extendido entran revisores y también editores y admins que en este
  // proyecto solo revisan; al operativo, solo admin/editor), menos los que
  // ya están en el proyecto. Se separan para que el picker vacío diga la
  // causa real.
  const eligibleUsers = useMemo<TenantUserSummary[]>(
    () => (usersQuery.data ?? []).filter((u) => canJoinSection(u.role, mode)),
    [usersQuery.data, mode],
  )
  const availableUsers = useMemo<TenantUserSummary[]>(() => {
    const taken = new Set(
      existingMembers
        .filter((m) => m.user_id !== null)
        .map((m) => m.user_id as string),
    )
    return eligibleUsers.filter((u) => !taken.has(u.id))
  }, [eligibleUsers, existingMembers])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      if (!userId) {
        setError(t('team.add_internal_dialog.errors.user_required'))
        throw new Error('validation')
      }
      const body = {
        roles: selectedRoles,
        user_id: userId,
        section: mode,
        ...(raci ? { raci_role: raci } : {}),
        subscribe_all_events: subscribeAll,
      }
      return addProjectMember(accessToken, projectId, body)
    },
    onSuccess: async (response) => {
      const user = availableUsers.find((u) => u.id === response.member.user_id)
      const displayName = user?.display_name ?? user?.email ?? 'Miembro'
      pushToast({
        kind: 'success',
        message: t('team.toasts.added', { name: displayName }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'members'],
      })

      // Solo tiene sentido ofrecerlo a quien puede ser dueño (D12), y
      // solo si el ciclo de vida declara documentos de ese rol que
      // siguen sin responsable. Si algo falla al consultarlo, se cierra
      // sin ruido: el alta ya funcionó, y el atajo es una comodidad, no
      // parte de la operación.
      const newUserId = response.member.user_id
      if (accessToken && newUserId && canOwnArtifact(user?.role)) {
        try {
          // Documentos sin responsable de cualquiera de sus roles.
          const perRole = await Promise.all(
            selectedRoles.map((r) => listUnownedByRole(accessToken, projectId, r)),
          )
          const pending = [
            ...new Map(perRole.flat().map((a) => [a.id, a])).values(),
          ]
          if (pending.length > 0) {
            setPendingAssignment({
              userId: newUserId,
              displayName,
              roleKeys: selectedRoles,
              artifacts: pending,
            })
            return
          }
        } catch {
          // Sin atajo. El alta ya está hecha.
        }
      }
      reset()
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'validation') return
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.add_internal_dialog.errors.submit_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      setError(message)
    },
  })

  const bulkMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken || !pendingAssignment) throw new Error('no_context')
      // Uno por rol: el endpoint asigna los documentos de un rol a la vez.
      let assigned = 0
      for (const roleKey of pendingAssignment.roleKeys) {
        const result = await assignOwnerByRole(accessToken, projectId, {
          role_template_key: roleKey,
          owner_user_id: pendingAssignment.userId,
        })
        assigned += result.assigned_count
      }
      return assigned
    },
    onSuccess: (assigned) => {
      pushToast({
        kind: 'success',
        message: t('team.bulk_assign.toast_success', {
          count: assigned,
          name: pendingAssignment?.displayName ?? '',
        }),
      })
      // Los artefactos cambiaron de dueño: el drill-in de fase los lee.
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'artifacts'],
      })
      reset()
      onClose()
    },
    onError: () => {
      pushToast({
        kind: 'critical',
        message: t('team.bulk_assign.toast_failed'),
        duration: 6000,
      })
    },
  })

  function reset() {
    setUserId('')
    setPendingAssignment(null)
    setSelectedRoles(defaultRoles)
    setRaci('')
    setSubscribeAll(false)
    setError(null)
  }

  function handleClose() {
    if (mutation.isPending || bulkMutation.isPending) return
    reset()
    onClose()
  }

  const submitting = mutation.isPending

  // Segundo paso: el miembro ya está en el equipo y quedan documentos de
  // su rol sin responsable. El alta NO se deshace si dice que no.
  if (pendingAssignment) {
    const count = pendingAssignment.artifacts.length
    const assigning = bulkMutation.isPending
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        title={t('team.bulk_assign.title')}
        footer={
          <>
            <button
              type="button"
              onClick={handleClose}
              disabled={assigning}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('team.bulk_assign.skip')}
            </button>
            <button
              type="button"
              onClick={() => bulkMutation.mutate()}
              disabled={assigning}
              className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50"
            >
              {assigning
                ? t('team.bulk_assign.assigning')
                : t('team.bulk_assign.confirm', { count })}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="font-body text-sm text-ink">
            {t('team.bulk_assign.question', {
              count,
              name: pendingAssignment.displayName,
            })}
          </p>
          <ul className="max-h-56 overflow-y-auto rounded-md border border-line divide-y divide-line">
            {pendingAssignment.artifacts.map((a) => (
              <li
                key={a.id}
                className="flex items-baseline justify-between gap-3 px-3 py-2"
              >
                <span className="font-body text-sm text-ink">{a.name}</span>
                {a.subphase_code && (
                  <span className="font-mono text-[11px] text-ink-subtle shrink-0">
                    {a.subphase_code}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-ink-subtle">
            {t('team.bulk_assign.help')}
          </p>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t(titleKey)}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('team.remove_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={
              submitting ||
              !userId ||
              availableUsers.length === 0 ||
              selectedRoles.length === 0
            }
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? t('team.add_internal_dialog.submitting')
              : t('team.add_internal_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.add_internal_dialog.user_label')}
          </span>
          {usersQuery.isLoading ? (
            <p className="mt-2 font-mono text-xs text-ink-subtle">
              {t('team.add_internal_dialog.loading_users')}
            </p>
          ) : availableUsers.length === 0 ? (
            <p className="mt-2 font-body text-sm text-ink-muted italic">
              {eligibleUsers.length === 0
                ? t(`team.add_internal_dialog.no_eligible_license_${mode}`)
                : t('team.add_internal_dialog.no_users_available')}
            </p>
          ) : (
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={submitting}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
            >
              <option value="">
                {t('team.add_internal_dialog.user_placeholder')}
              </option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} · {u.email}
                </option>
              ))}
            </select>
          )}
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('team.add_internal_dialog.user_help')}
          </span>
        </label>

        <div className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.add_internal_dialog.role_label')}
          </span>
          <RoleChecklist
            roles={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            disabled={submitting}
            ariaLabel={t('team.add_internal_dialog.role_label')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">
              {t('team.add_internal_dialog.raci_label')}
            </span>
            <select
              value={raci}
              onChange={(e) => setRaci(e.target.value as RaciRole | '')}
              disabled={submitting}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('team.raci_options.none')}</option>
              <option value="R">{t('team.raci_options.R')}</option>
              <option value="A">{t('team.raci_options.A')}</option>
              <option value="C">{t('team.raci_options.C')}</option>
              <option value="I">{t('team.raci_options.I')}</option>
            </select>
          </label>
        </div>

        <label className="flex items-start gap-2 rounded-md border border-line bg-surface-muted/40 p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={subscribeAll}
            onChange={(e) => setSubscribeAll(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 w-4 h-4 rounded border-line text-brand focus:ring-brand focus:ring-offset-0"
          />
          <span className="text-xs">
            <span className="block font-medium text-ink">
              {t('team.add_internal_dialog.subscribe_all_label')}
            </span>
            <span className="block text-ink-muted mt-0.5">
              {t('team.add_internal_dialog.subscribe_all_help')}
            </span>
          </span>
        </label>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
